import { Banner } from '@/modules/adv/banner'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { AdProviderNames } from '@/types'
import { timer } from '@/utils/helpers'
import { noop } from '@/utils/noop'

import { AdProvider, AdProviderConfig, AdProviderStatus, NotImplementedError, OutOfPageAdType } from '../..'
import {
    AdSenseAdBreakDonePlacementInfo,
    AdSenseAdBreakFunctionCommonParams,
    AdSenseAdBreakFunctionParam,
} from './AdSenseAdBreak'
import { OUT_OF_PAGE_AD_TYPES_SUPPORTED_BY_AD_SENSE_PROVIDER, isSupportedOutOfPageAdType } from './constants'
import { AdSenseScriptFunctions, loadAdSenseScript } from './loadAdSenseScript'
import { preloadFirstAdBreak } from './preloadFirstAdBreak'

/**
 * Timeout for ad breaks preloading
 * https://developers.google.com/ad-placement/docs/manual-sequence
 */
const PRELOAD_AD_BREAKS_TIMEOUT_MS = 3_000

const REWARD_AMOUNT_FOR_AD_SENSE_REWARDED = 1

type AdBreakStopReason = 'viewed' | 'not_started' | 'reward_dismissed' | 'reward_completed' | 'unknown'

export class GoogleAdSenseAdProvider implements AdProvider {
    readonly loadScriptPromise: Promise<AdSenseScriptFunctions>

    readonly serviceStatus: Promise<AdProviderStatus> = (async () => {
        try {
            await this.loadScriptPromise

            return AdProviderStatus.online
        } catch {
            return AdProviderStatus.offline
        }
    })()

    private readonly abortController = new AbortController()

    private isFirstAdBreakPreloaded = false

    constructor(config: AdProviderConfig) {
        this.loadScriptPromise = loadAdSenseScript({
            exp: config.targeting?.exp,
        })
    }

    // eslint-disable-next-line class-methods-use-this
    getAdUnitByType() {
        return undefined
    }

    prepareOutOfPageAd(type: OutOfPageAdType, timeout?: number): Promise<OutOfPageBanner> {
        if (!isSupportedOutOfPageAdType(type)) {
            throw new Error(`Unsupported out of page ad type for Google AdSense: ${type}`)
        }

        const banner = new OutOfPageBanner({
            provider: AdProviderNames.GOOGLE_AD_SENSE,
        })

        banner.addEventListener('closed', () => {
            banner.destroy()
            this.abortController.abort()
        })

        // Google AdSense script is changing the hash of the page and reverts it back at the end of the ad break
        // But, since the game is run inside of friendly iframe, this can cause to the navigation and refresh of the whole page
        // https://gist.github.com/senaev/ea26b52374b287e904a2c30b2b2ad2a7#file-show_ads_impl_fy2021-js-L29005
        // This workaround is dedicated to prevent this behavior
        // Restrict backward navigation during ad break and restore the original method afterwards
        const originalHistoryBackMethod = window.history.back
        window.history.back = noop

        let isAdBreakStarted = false
        this.showBanner({
            type,
            timeout,
            banner,
            onBeforeAd: () => {
                isAdBreakStarted = true
            },
        })
            .then((reason) => {
                if (reason === 'viewed') {
                    banner.triggerClosed()
                    return
                }

                if (reason === 'reward_dismissed') {
                    banner.triggerClosed()
                    return
                }

                if (reason === 'reward_completed') {
                    banner.triggerRewarded(REWARD_AMOUNT_FOR_AD_SENSE_REWARDED)

                    timer(0).then(() => {
                        banner.triggerClosed()
                    })

                    return
                }

                if (reason === 'not_started') {
                    banner.triggerEmpty()
                    return
                }

                // Trigger close in case of unknown reason
                // TODO: log these cases
                banner.triggerClosed()
            })
            .catch(() => {
                // TODO: add telemetry
                if (isAdBreakStarted) {
                    banner.triggerClosed()
                    return
                }

                banner.triggerEmpty()
            })
            .finally(() => {
                window.history.back = originalHistoryBackMethod
            })

        return Promise.resolve(banner)
    }

    // eslint-disable-next-line class-methods-use-this
    updateTargeting(): void {
        //
    }

    // eslint-disable-next-line class-methods-use-this
    requestPageAd(): Promise<Banner> {
        return Promise.reject(new NotImplementedError())
    }

    private async showBanner({
        timeout,
        type,
        banner,
        onBeforeAd,
    }: {
        timeout?: number
        type: OUT_OF_PAGE_AD_TYPES_SUPPORTED_BY_AD_SENSE_PROVIDER
        banner: OutOfPageBanner
        onBeforeAd: VoidFunction
    }): Promise<AdBreakStopReason> {
        const timeoutMs = timeout ?? PRELOAD_AD_BREAKS_TIMEOUT_MS

        const adSenseScriptFunctions = await this.loadScriptPromise

        const { adBreak } = adSenseScriptFunctions

        if (!this.isFirstAdBreakPreloaded) {
            this.isFirstAdBreakPreloaded = true

            await preloadFirstAdBreak(adSenseScriptFunctions, {
                abortController: this.abortController,
                timeoutMs,
            })
        }

        const getAdBreakStopReasonByAdBreakDonePlacementInfo = (
            placementInfo: AdSenseAdBreakDonePlacementInfo,
        ): AdBreakStopReason => {
            const { breakStatus } = placementInfo

            if (breakStatus === 'viewed') {
                if (type === 'rewarded') {
                    return 'reward_completed'
                }

                return 'viewed'
            }

            if (breakStatus === 'other' || breakStatus === 'frequencyCapped') {
                return 'not_started'
            }

            if (breakStatus === 'dismissed') {
                return 'reward_dismissed'
            }

            return 'unknown'
        }

        return new Promise<AdBreakStopReason>((resolve) => {
            const commonAdBreakCallbacks: {
                beforeAd: AdSenseAdBreakFunctionCommonParams['beforeAd']
                adBreakDone: AdSenseAdBreakFunctionCommonParams['adBreakDone']
            } = {
                beforeAd: () => {
                    onBeforeAd()

                    // AdSense doesn't have a "viewable" event, so we consider the ad to be shown when it's loaded
                    // For reliability, we added a zero timeout
                    timer(0).then(() => {
                        banner.triggerViewable()
                    })
                },
                adBreakDone: (placementInfo) => {
                    const stopReason: AdBreakStopReason = getAdBreakStopReasonByAdBreakDonePlacementInfo(placementInfo)

                    resolve(stopReason)
                },
            }

            const AD_BREAK_PARAMETERS: {
                [key in OUT_OF_PAGE_AD_TYPES_SUPPORTED_BY_AD_SENSE_PROVIDER]: AdSenseAdBreakFunctionParam
            } = {
                interstitial_preroll: {
                    type: 'start',
                    name: 'start-ad-break-for-game-preroll',
                    ...commonAdBreakCallbacks,
                },
                interstitial: {
                    type: 'next',
                    name: 'start-ad-break-for-game-interstitial',
                    ...commonAdBreakCallbacks,
                },
                rewarded: {
                    type: 'reward',
                    name: 'start-ad-break-for-game-rewarded',
                    ...commonAdBreakCallbacks,
                    beforeReward: (showRewardAd) => {
                        // User has already agreed to watch the ad,
                        // so we don't ask anything additional, just show it
                        showRewardAd()
                    },
                    adDismissed: noop,
                    adViewed: noop,
                },
            }

            banner.triggerReady(() => {
                adBreak(AD_BREAK_PARAMETERS[type])
            })
        })
    }
}
